import { DriveStep, Driver, DriverHook } from "driver.js";
import i18next from "i18next";
import { logger } from "../../util/Logger";

interface DemoContext {
  root: HTMLElement;
  driver: Driver;
  cancelled: () => boolean;
  sleep: (ms: number) => Promise<void>;
  refresh: () => void;
  advance: () => void;
  /** Close tip only; keep template start page open; resume later at Şablonlar. */
  dismissTipKeepStartModal: () => void;
}

interface StepDef {
  selector?: string;
  resolve?: (root: HTMLElement) => Element | null;
  titleKey: string;
  descKey: string;
  side: "top" | "right" | "bottom" | "left" | "over";
  align: "start" | "center" | "end";
  /** Async demo while the tip is visible. */
  demo?: (ctx: DemoContext) => Promise<void>;
  /** After demo finishes, move to the next step automatically. */
  autoAdvance?: boolean;
  /** Close Boyutlandır / right pane when the user leaves this step (Next). */
  closeFeatOnDeselect?: boolean;
  /** Close Teklif Al modal when the user leaves this step (Next). */
  closeQuoteOnDeselect?: boolean;
  id?: string;
}

export type OnboardingStartAt = "welcome" | "templates";

/** True while the first-visit template start page is open. */
export function isStartModalOpen(root: HTMLElement | null): boolean {
  if (!root) {
    return Boolean(document.querySelector("#draw-from-scratch-button"));
  }
  return Boolean(
    root.querySelector("#draw-from-scratch-button") ||
      document.querySelector("#draw-from-scratch-button"),
  );
}

let demoGeneration = 0;

export function cancelOnboardingDemos(): void {
  demoGeneration += 1;
}

function isHighlightable(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 1 && rect.height > 1;
}

function queryInRoot(root: HTMLElement, selector: string): Element | null {
  return root.querySelector(selector) || document.querySelector(selector);
}

function clickEl(root: HTMLElement, selector: string): boolean {
  const el = queryInRoot(root, selector);
  if (!(el instanceof HTMLElement)) return false;
  el.click();
  return true;
}

function closeRightPane(root: HTMLElement): void {
  const rightPane = queryInRoot(root, "#right-pane") as
    | (HTMLElement & { closePane?: () => void })
    | null;
  if (rightPane?.hasAttribute("data-open")) {
    rightPane.closePane?.();
  }
}

function closeTemplatesModal(root: HTMLElement): void {
  clickEl(root, ".room-shaping-close");
}

function closeQuoteModal(root: HTMLElement): void {
  const modal = queryInRoot(root, "sugar-request-modal");
  modal?.dispatchEvent(
    new CustomEvent("request-modal-close", {
      bubbles: true,
      composed: true,
    }),
  );
}

/** Always the left-toolbar products control (6th icon), not the open right pane. */
function resolveProductsStep(root: HTMLElement): Element | null {
  const btn = queryInRoot(root, "#button-products");
  if (btn instanceof HTMLElement) {
    btn.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  return btn;
}

function resolveViewModeToggle(root: HTMLElement): Element | null {
  const chrome = queryInRoot(root, "#view-mode-toggle");
  if (isHighlightable(chrome)) return chrome;
  const mobile = queryInRoot(root, "#view-mode-toggle-mobile");
  if (isHighlightable(mobile)) return mobile;
  return chrome ?? mobile;
}

function resolveFeatStep(root: HTMLElement): Element | null {
  const openPane = queryInRoot(root, "#right-pane[data-open]");
  const featSelected = queryInRoot(root, "#button-feat.selected");
  if (openPane && featSelected && isHighlightable(openPane)) return openPane;
  return queryInRoot(root, "#button-feat");
}

function openProductPaneLikeClick(root: HTMLElement): void {
  const btn = queryInRoot(root, "#button-products");
  if (btn instanceof HTMLElement && !btn.classList.contains("selected")) {
    btn.click();
    return;
  }
  const rightPane = queryInRoot(root, "#right-pane") as
    | (HTMLElement & { toggleProductPane?: () => void })
    | null;
  if (!rightPane?.hasAttribute("data-open")) {
    rightPane?.toggleProductPane?.();
  }
}

/** Animate two wall length sliders one-by-one over ~10s. */
async function randomizeRoomDimensions(
  root: HTMLElement,
  sleep: (ms: number) => Promise<void>,
): Promise<void> {
  const ranges = Array.from(
    root.querySelectorAll<HTMLInputElement>(
      ".room-input-range:not(.room-thickness-range)",
    ),
  ).filter((input) => !input.disabled);
  const targets = ranges.slice(0, Math.min(2, ranges.length));
  if (targets.length === 0) return;

  const totalMs = 10_000;
  const perWallMs = Math.floor(totalMs / targets.length);
  const stepsPerWall = 10;
  const stepDelay = Math.floor(perWallMs / (stepsPerWall + 1));

  for (const input of targets) {
    const min = Number(input.min) || 0.5;
    const max = Number(input.max) || 15;
    const from = Number(input.value) || min;
    // Stay within ±30% of the current wall length.
    const maxDelta = Math.max(0.1, from * 0.3);
    const delta = maxDelta * (0.55 + Math.random() * 0.45);
    const direction = from > (min + max) / 2 ? -1 : 1;
    let to = Math.round((from + direction * delta) * 10) / 10;
    to = Math.min(max, Math.max(min, to));
    const clampedDelta = Math.min(Math.abs(to - from), maxDelta);
    to = Math.round((from + direction * clampedDelta) * 10) / 10;
    to = Math.min(max, Math.max(min, to));

    input.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true }));
    for (let i = 1; i <= stepsPerWall; i++) {
      const value =
        Math.round((from + ((to - from) * i) / stepsPerWall) * 10) / 10;
      input.value = String(value);
      input.dispatchEvent(new Event("input", { bubbles: true }));
      await sleep(stepDelay);
    }
    input.dispatchEvent(new Event("change", { bubbles: true }));
    await sleep(stepDelay);
  }
}

/** First-visit start modal actions: "Sıfırdan çiz" + "Şablonu Seç". */
function resolveWelcomeActions(root: HTMLElement): Element | null {
  const actions = queryInRoot(root, "#room-shaping-start-actions");
  if (
    actions &&
    queryInRoot(root, "#draw-from-scratch-button") &&
    queryInRoot(root, "#confirm-shaping-button")
  ) {
    return actions;
  }
  // Both CTA buttons must be present — do not fall back to the toolbar icon.
  return queryInRoot(root, ".room-start-modal #room-shaping-start-actions");
}

async function demoWelcome(ctx: DemoContext): Promise<void> {
  closeRightPane(ctx.root);
  closeQuoteModal(ctx.root);
  if (!queryInRoot(ctx.root, "#draw-from-scratch-button")) {
    clickEl(ctx.root, "#button-templates");
  }
  // Wait until both start CTAs are in the DOM, then spotlight them.
  for (let i = 0; i < 40; i++) {
    if (
      queryInRoot(ctx.root, "#draw-from-scratch-button") &&
      queryInRoot(ctx.root, "#confirm-shaping-button") &&
      isHighlightable(queryInRoot(ctx.root, "#room-shaping-start-actions"))
    ) {
      break;
    }
    await ctx.sleep(50);
  }
  ctx.refresh();

  // Keep tip up to 10s. Do not close the template page — only dismiss the tip.
  const deadline = Date.now() + 10_000;
  while (Date.now() < deadline) {
    if (ctx.cancelled()) return;
    // User finished with the start page early — close tip, resume Şablonlar next.
    if (!isStartModalOpen(ctx.root)) {
      ctx.dismissTipKeepStartModal();
      return;
    }
    await ctx.sleep(200);
  }
  ctx.dismissTipKeepStartModal();
}

/** Left-toolbar templates control (Şablonlar) — 8th icon, not feat/products. */
function resolveTemplatesToolbarButton(root: HTMLElement): Element | null {
  // Never highlight while the start modal still covers the toolbar.
  if (isStartModalOpen(root)) return null;
  const btn = queryInRoot(root, "#button-templates");
  if (btn instanceof HTMLElement) {
    btn.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  return btn;
}

async function demoTemplatesHint(ctx: DemoContext): Promise<void> {
  closeRightPane(ctx.root);
  // Do NOT close the template start page — wait until the user closes it.
  while (isStartModalOpen(ctx.root)) {
    if (ctx.cancelled()) return;
    await ctx.sleep(200);
  }
  const btn = queryInRoot(ctx.root, "#button-templates");
  if (btn instanceof HTMLElement) {
    btn.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  await ctx.sleep(200);
  ctx.refresh();
}

async function demoProducts(ctx: DemoContext): Promise<void> {
  const btn = queryInRoot(ctx.root, "#button-products");
  if (btn instanceof HTMLElement) {
    btn.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  await ctx.sleep(200);
  ctx.refresh();
  openProductPaneLikeClick(ctx.root);
  await ctx.sleep(350);
  // Keep spotlight on #button-products (do not retarget to #right-pane).
  ctx.refresh();
}

async function demoFeatResize(ctx: DemoContext): Promise<void> {
  // Only close start modal if still open when reaching Boyutlandır.
  closeTemplatesModal(ctx.root);
  closeRightPane(ctx.root);
  await ctx.sleep(200);
  const feat = queryInRoot(ctx.root, "#button-feat");
  if (feat instanceof HTMLElement && !feat.classList.contains("selected")) {
    feat.click();
  }
  await ctx.sleep(550);
  ctx.refresh();

  // Animate wall lengths one-by-one (~10s) when segments exist.
  const wallRanges = ctx.root.querySelectorAll(
    ".room-input-range:not(.room-thickness-range):not([disabled])",
  );
  if (wallRanges.length > 0) {
    await randomizeRoomDimensions(ctx.root, ctx.sleep);
  }
  // Keep Boyutlandır open until the user clicks Next.
}

async function demoFullscreen(ctx: DemoContext): Promise<void> {
  const btn = queryInRoot(ctx.root, "#button-fullscreen");
  if (!(btn instanceof HTMLElement)) return;

  // Tip + spotlight first; open/close only after the popover is readable.
  await ctx.sleep(1400);
  ctx.refresh();

  const wasOpen = btn.classList.contains("selected");
  if (!wasOpen) {
    btn.click();
    await ctx.sleep(3200);
    ctx.refresh();
    await ctx.sleep(2800);
  }
  if (btn.classList.contains("selected")) {
    btn.click();
    await ctx.sleep(1800);
    ctx.refresh();
  }
}

async function demoZoom(ctx: DemoContext): Promise<void> {
  clickEl(ctx.root, "#button-zoom-out");
  await ctx.sleep(350);
  clickEl(ctx.root, "#button-zoom-out");
  await ctx.sleep(350);
  clickEl(ctx.root, "#button-zoom-in");
  await ctx.sleep(350);
  clickEl(ctx.root, "#button-zoom-in");
  await ctx.sleep(200);
}

function resolveQuoteStep(root: HTMLElement): Element | null {
  const btn = queryInRoot(root, "#button-quote");
  if (btn instanceof HTMLElement) {
    btn.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  return btn;
}

async function demoQuote(ctx: DemoContext): Promise<void> {
  closeRightPane(ctx.root);
  const btn = queryInRoot(ctx.root, "#button-quote");
  if (btn instanceof HTMLElement) {
    btn.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  // Let the spotlight settle on the left-toolbar icon first.
  await ctx.sleep(500);
  ctx.refresh();
  await ctx.sleep(700);

  const leftPane = queryInRoot(ctx.root, "left-pane") ?? ctx.root;
  leftPane.dispatchEvent(
    new CustomEvent("room-quote-request", {
      bubbles: true,
      composed: true,
      detail: { force: true },
    }),
  );
  await ctx.sleep(400);
  // Keep highlighting #button-quote (not the modal).
  ctx.refresh();
}

/**
 * Selectors must match the current Room Designer chrome.
 * Order follows the product onboarding script.
 */
const STEP_DEFS: StepDef[] = [
  {
    id: "welcome",
    resolve: resolveWelcomeActions,
    titleKey: "tips.onboarding.welcomeTitle",
    descKey: "tips.onboarding.welcomeDesc",
    side: "right",
    align: "center",
    demo: demoWelcome,
  },
  {
    id: "templates",
    resolve: resolveTemplatesToolbarButton,
    titleKey: "tips.onboarding.templatesTitle",
    descKey: "tips.onboarding.templatesDesc",
    side: "right",
    align: "start",
    demo: demoTemplatesHint,
  },
  {
    resolve: resolveFeatStep,
    titleKey: "tips.onboarding.resizeTitle",
    descKey: "tips.onboarding.resizeDesc",
    side: "right",
    align: "start",
    demo: demoFeatResize,
    closeFeatOnDeselect: true,
  },
  {
    resolve: resolveProductsStep,
    titleKey: "tips.onboarding.panelTitle",
    descKey: "tips.onboarding.panelDesc",
    side: "right",
    align: "start",
    demo: demoProducts,
  },
  {
    resolve: resolveViewModeToggle,
    titleKey: "tips.onboarding.viewModeTitle",
    descKey: "tips.onboarding.viewModeDesc",
    side: "bottom",
    align: "center",
  },
  {
    selector: "#button-draw-wall",
    titleKey: "tips.onboarding.drawWallTitle",
    descKey: "tips.onboarding.drawWallDesc",
    side: "right",
    align: "start",
  },
  {
    selector: "#button-window",
    titleKey: "tips.onboarding.windowTitle",
    descKey: "tips.onboarding.windowDesc",
    side: "right",
    align: "start",
  },
  {
    selector: "#button-door",
    titleKey: "tips.onboarding.doorTitle",
    descKey: "tips.onboarding.doorDesc",
    side: "right",
    align: "start",
  },
  {
    selector: "#button-fullscreen",
    titleKey: "tips.onboarding.fullscreenTitle",
    descKey: "tips.onboarding.fullscreenDesc",
    side: "bottom",
    align: "end",
    demo: demoFullscreen,
    autoAdvance: true,
  },
  {
    selector: "#top-chrome-history",
    titleKey: "tips.onboarding.undoTitle",
    descKey: "tips.onboarding.undoDesc",
    side: "bottom",
    align: "start",
  },
  {
    selector: "#top-chrome-zoom-group",
    titleKey: "tips.onboarding.zoomTitle",
    descKey: "tips.onboarding.zoomDesc",
    side: "bottom",
    align: "center",
    demo: demoZoom,
  },
  {
    resolve: resolveQuoteStep,
    titleKey: "tips.onboarding.quoteTitle",
    descKey: "tips.onboarding.quoteDesc",
    side: "right",
    align: "end",
    demo: demoQuote,
    closeQuoteOnDeselect: true,
  },
];

function resolveStepElement(
  root: HTMLElement,
  def: StepDef,
): Element | null {
  if (def.resolve) return def.resolve(root);
  if (!def.selector) return null;
  return queryInRoot(root, def.selector);
}

function bindStepDemo(
  root: HTMLElement,
  def: StepDef,
  onDismissKeepStartModal: () => void,
): DriverHook | undefined {
  if (!def.demo) return undefined;
  const demo = def.demo;
  const autoAdvance = Boolean(def.autoAdvance);

  return async (_element, _step, { driver }) => {
    const gen = ++demoGeneration;
    const ctx: DemoContext = {
      root,
      driver,
      cancelled: () => gen !== demoGeneration,
      sleep: async (ms: number) => {
        await new Promise<void>((resolve) => setTimeout(resolve, ms));
        if (gen !== demoGeneration) {
          throw new Error("tour-demo-cancelled");
        }
      },
      refresh: () => {
        if (gen !== demoGeneration) return;
        try {
          driver.refresh();
        } catch {
          /* tour closed */
        }
      },
      advance: () => {
        if (gen !== demoGeneration) return;
        try {
          if (driver.hasNextStep()) driver.moveNext();
          else driver.destroy();
        } catch {
          /* tour closed */
        }
      },
      dismissTipKeepStartModal: () => {
        if (gen !== demoGeneration) return;
        onDismissKeepStartModal();
      },
    };

    try {
      await demo(ctx);
      if (autoAdvance) ctx.advance();
    } catch (error) {
      if ((error as Error)?.message !== "tour-demo-cancelled") {
        logger.warn("[TipManager] Onboarding demo failed", error);
      }
    }
  };
}

/** Close tour-owned UI (templates / panes / quote). */
export function cleanupOnboardingUi(
  root: HTMLElement | null,
  options?: { keepStartModal?: boolean },
): void {
  cancelOnboardingDemos();
  if (!root) return;
  if (!options?.keepStartModal) {
    closeTemplatesModal(root);
  }
  closeRightPane(root);
  closeQuoteModal(root);
  const fs = queryInRoot(root, "#button-fullscreen.selected");
  if (fs instanceof HTMLElement) fs.click();
}

export function getOnboardingSteps(
  root: HTMLElement,
  options?: {
    startAt?: OnboardingStartAt;
    onDismissWelcomeKeepStartModal?: () => void;
  },
): DriveStep[] {
  const steps: DriveStep[] = [];
  const startAt = options?.startAt ?? "welcome";
  let started = startAt === "welcome";

  for (const def of STEP_DEFS) {
    if (!started) {
      if (def.id === startAt) started = true;
      else continue;
    }

    const hasTarget = Boolean(def.selector || def.resolve);
    if (hasTarget) {
      const el = resolveStepElement(root, def);
      // Welcome / templates may wait for modal lifecycle — allow deferred resolve.
      const allowDeferred =
        (def.id === "welcome" || def.id === "templates") && Boolean(def.demo);
      if (!isHighlightable(el) && !allowDeferred) {
        logger.warn(
          `[TipManager] Onboarding step skipped — element not highlightable: "${
            def.selector ?? def.titleKey
          }"`,
        );
        continue;
      }
    }

    const isWelcome = def.id === "welcome";
    const dismissKeepModal = options?.onDismissWelcomeKeepStartModal;

    const step: DriveStep = {
      popover: {
        title: i18next.t(def.titleKey),
        description: i18next.t(def.descKey),
        side: def.side,
        align: def.align,
        ...(isWelcome && dismissKeepModal
          ? {
              // Next / X = close tip only, keep template start page open.
              onNextClick: () => {
                dismissKeepModal();
              },
              onCloseClick: () => {
                dismissKeepModal();
              },
            }
          : {}),
      },
      onHighlightStarted: bindStepDemo(root, def, () => {
        dismissKeepModal?.();
      }),
      onDeselected: () => {
        cancelOnboardingDemos();
        if (def.closeFeatOnDeselect) {
          if (queryInRoot(root, "#button-feat.selected")) {
            clickEl(root, "#button-feat");
          } else {
            closeRightPane(root);
          }
        }
        if (def.closeQuoteOnDeselect) {
          closeQuoteModal(root);
        }
      },
    };

    if (hasTarget) {
      step.element = () =>
        (resolveStepElement(root, def) ||
          queryInRoot(root, "#button-templates")) as Element;
    }

    steps.push(step);
  }

  return steps;
}
